import { useId, useState, type ReactNode } from "react";
import { ScreenScaffold, type ScreenState } from "../ScreenScaffold.tsx";
import { useIdentity, roleAtLeast } from "../../state/identity.tsx";
import { DenseTable, StatusBadge, type Column } from "../../components/index.ts";
import { formatDateTime, isoAttr } from "../../lib/datetime.ts";
import { useAgentsPaged, useToolsPaged, useAgentDnaMetrics, useAgentRisk } from "./data.ts";
import { MetricTiles, statusLevel } from "./components.tsx";
import { DetailDrawer, DrawerSection, KeyValue } from "./DetailDrawer.tsx";
import { AgentRiskPanel } from "./AgentRiskPanel.tsx";
import type { Agent, Tool } from "./types.ts";
import { AppIcon } from "./AppIcon.tsx";
import "./agentdna.css";

type Tab = "agents" | "apps";

/**
 * Agents & Apps (AgentDNA port): the directory of agents and the apps/tools they use. Two tabs over
 * the same screen — agents (with reliability score, interactions, threats, environment, owner) and
 * apps/tools (score, interactions, provider, scope). Viewer-level; the server enforces access.
 *
 * UI-only port, wired to contract-shaped stubs until the real reads land.
 */
export function AgentsAppsScreen(): ReactNode {
  const { role } = useIdentity();
  const [tab, setTab] = useState<Tab>("agents");
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const { data: agents, loading: agentsLoading } = useAgentsPaged(1);
  const { data: tools, loading: toolsLoading } = useToolsPaged(1);
  const { data: metrics } = useAgentDnaMetrics();

  // The agent-risk read for the open drawer: keyed on the selected agent's id and `enabled` only
  // while a drawer is open, so it fires on open and stops on close (requirement 13.5).
  const riskQuery = useAgentRisk(selectedAgent?.id ?? null);
  const drawerTitleId = useId();

  const loading = tab === "agents" ? agentsLoading : toolsLoading;
  const count = tab === "agents" ? agents.items.length : tools.items.length;
  const state = resolveState(role, loading, count);

  const agentColumns: readonly Column<Agent>[] = [
    { key: "name", header: "Agent", isRowHeader: true, cell: (r) => r.name },
    {
      key: "status",
      header: "Status",
      cell: (r) => <StatusBadge level={statusLevel(r.status)} size="sm" />,
    },
    { key: "score", header: "Reliability", align: "right", cell: (r) => `${r.score}` },
    { key: "interactions", header: "Interactions", align: "right", cell: (r) => r.interactions },
    { key: "threats", header: "Threats", align: "right", cell: (r) => r.threats },
    { key: "env", header: "Env", cell: (r) => r.env },
    { key: "owner", header: "Owner", cell: (r) => r.owner },
    {
      key: "created",
      header: "Created",
      align: "right",
      cell: (r) => <time dateTime={isoAttr(r.created)}>{formatDateTime(r.created)}</time>,
    },
  ];

  const toolColumns: readonly Column<Tool>[] = [
    {
      key: "name",
      header: "App / Tool",
      isRowHeader: true,
      cell: (r) => (
        <span className="adna-app-cell">
          <AppIcon name={r.name} />
          {r.name}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (r) => <StatusBadge level={statusLevel(r.status)} size="sm" />,
    },
    { key: "score", header: "Reliability", align: "right", cell: (r) => `${r.score}` },
    { key: "interactions", header: "Interactions", align: "right", cell: (r) => r.interactions },
    { key: "threats", header: "Threats", align: "right", cell: (r) => r.threats },
    { key: "provider", header: "Provider", cell: (r) => r.provider },
    { key: "scope", header: "Scope", cell: (r) => r.scope },
  ];

  return (
    <ScreenScaffold
      title="Agents & Apps"
      subtitle="The agents in your organization and the apps and tools they use."
      state={state}
    >
      <MetricTiles
        tiles={[
          { label: "Agents", value: metrics.agentCount },
          { label: "Apps & tools", value: metrics.appCount },
          { label: "Avg reliability", value: metrics.avgReliability },
          { label: "Threats", value: metrics.threatCount },
        ]}
      />
      <div className="adna-tabs" role="tablist" aria-label="Agents or apps">
        <button
          type="button"
          role="tab"
          className="adna-tab"
          aria-selected={tab === "agents"}
          onClick={() => setTab("agents")}
        >
          Agents
        </button>
        <button
          type="button"
          role="tab"
          className="adna-tab"
          aria-selected={tab === "apps"}
          onClick={() => setTab("apps")}
        >
          Apps & tools
        </button>
      </div>
      {tab === "agents" ? (
        <DenseTable
          caption="Agents"
          columns={agentColumns}
          rows={agents.items}
          rowKey={(r) => r.id}
          onRowClick={(r) => setSelectedAgent(r)}
          rowLabel={(r) => `Open the risk detail for agent ${r.name}`}
          emptyMessage="No agents enrolled yet."
        />
      ) : (
        <DenseTable
          caption="Apps and tools"
          columns={toolColumns}
          rows={tools.items}
          rowKey={(r) => r.id}
          emptyMessage="No apps or tools registered yet."
        />
      )}

      <DetailDrawer
        open={selectedAgent !== null}
        onClose={() => setSelectedAgent(null)}
        labelledBy={drawerTitleId}
      >
        {selectedAgent !== null && (
          <>
            <header className="drawer-head">
              <div className="drawer-head__title">
                <h2 className="drawer-head__heading" id={drawerTitleId}>
                  {selectedAgent.name}
                </h2>
                <span className="drawer-head__id">{selectedAgent.id}</span>
              </div>
              <button
                type="button"
                className="drawer-head__close"
                aria-label="Close agent detail"
                onClick={() => setSelectedAgent(null)}
              >
                ✕
              </button>
            </header>
            <div className="drawer-body">
              <DrawerSection title="Agent">
                <KeyValue
                  rows={[
                    {
                      k: "Status",
                      v: <StatusBadge level={statusLevel(selectedAgent.status)} size="sm" />,
                    },
                    { k: "Reliability", v: `${selectedAgent.score}` },
                    { k: "Interactions", v: selectedAgent.interactions },
                    { k: "Threats", v: selectedAgent.threats },
                    { k: "Environment", v: selectedAgent.env },
                    { k: "Owner", v: selectedAgent.owner },
                  ]}
                />
              </DrawerSection>
              <AgentRiskPanel query={riskQuery} />
            </div>
          </>
        )}
      </DetailDrawer>
    </ScreenScaffold>
  );
}

function resolveState(
  role: ReturnType<typeof useIdentity>["role"],
  loading: boolean,
  count: number,
): ScreenState {
  if (!roleAtLeast(role, "viewer")) {
    return {
      kind: "restricted",
      reason:
        role === "unknown"
          ? "Your role has not been resolved yet. The server enforces access regardless."
          : "Agents & Apps need at least the viewer role.",
    };
  }
  if (loading) return { kind: "loading" };
  if (count === 0) return { kind: "empty", cause: "Nothing registered for this organization yet." };
  return { kind: "ready" };
}
